import { useEffect, useState } from 'react';

import { head, isEmpty } from 'lodash';

import { useDispatch } from 'react-redux';

import { redirectTo, showAlert } from '../../../shared/reducers/page';
import { useAppSelector } from '../../../shared/store';
import { submitToCheckout, submitToCheckoutKurlyPass } from '../../../cart/shared/reducers/cart.slice';
import { selectCurrentTotalPrice, selectedDealProducts } from '../selectors';
import useCartItems from '../../../shared/components/Cart/hook/useCartItems';

import useSearchLocation from './useSearchLocation';

import { isPC } from '../../../../util/window/getDevice';

import { CART_PATH, COMMON_PATH, getPageUrl } from '../../../shared/constant';

import { DealProduct } from '../types';
import { DeliveryTimeType } from '../../../shared/interfaces';

import Alert from '../../../shared/components/Alert/Alert';
import { ProductDetailState } from '../slice';
import { AddToCartProduct } from '../../../shared/amplitude/events/product/AddToCartProduct';
import { AddToCartSuccess } from '../../../shared/amplitude/events/product/AddToCartSuccess';
import { amplitudeService } from '../../../shared/amplitude';
import { storeCartCounter } from '../../../cart/shared/services/cart-counter.storage.service';
import { AddToCartFail } from '../../../shared/amplitude/events/product/AddToCartFail';
import { branchService } from '../../../shared/branch';
import { AddToCart } from '../../../shared/branch/events';
import { getFusionQueryId } from '../shared/utils/productDetailEvent';
import Confirm, { closeConfirm } from '../../../shared/components/Alert/Confirm';
import useKurlyMember from '../../../shared/hooks/useKurlyMember';

interface EventAddToCartProductProps {
  productDetailState: ProductDetailState;
  defaultContentId: number;
  selectedProducts: DealProduct[];
  totalPrice: number;
  totalBasePrice: number;
  totalRetailPrice: number;
  fusionQueryId: string | null;
  filterId: string | null;
}

const eventAddToCartProduct = async ({
  productDetailState,
  defaultContentId,
  selectedProducts,
  totalPrice,
  totalBasePrice,
  totalRetailPrice,
  fusionQueryId,
  filterId,
}: EventAddToCartProductProps) => {
  selectedProducts.forEach((it) => {
    branchService.logEvent(
      new AddToCart([
        {
          $canonical_identifier: `product/${productDetailState.no}`,
          $sku: productDetailState.no,
          $price: it.discountedPrice || it.basePrice,
          $quantity: it.quantity,
          $currency: 'KRW',
          $product_name: productDetailState.name,
        },
      ]),
    );
    amplitudeService.syncLogEvent(
      new AddToCartProduct({
        product: productDetailState,
        defaultContentId: defaultContentId,
        dealProduct: it,
        totalPrice,
        totalBasePrice,
        totalRetailPrice,
        fusionQueryId,
        filterId,
      }),
    );
  });
};

interface Props {
  isSoldOut: boolean;
  isDirectOrder: boolean;
  isPurchaseStatus: boolean;
}

export default function useProductBuy({ isSoldOut, isDirectOrder, isPurchaseStatus }: Props) {
  const isGuest = useAppSelector(({ auth }) => auth.isGuest);
  const cartItems = useAppSelector(({ cart }) => cart.cartItems);
  const { queryId, filterId } = useAppSelector(({ productList }) => productList);
  const productDetailState = useAppSelector(({ productDetail }) => productDetail);
  const { currentAddress } = useAppSelector(({ shippingAddress }) => shippingAddress);
  const selectedProducts = useAppSelector(selectedDealProducts);
  const totalPrice = useAppSelector(selectCurrentTotalPrice);
  const { currentBenefitQuery } = useKurlyMember();
  const memberReserveRatio = currentBenefitQuery?.data?.percent ?? null;

  const totalBasePrice = selectedProducts.reduce((pre, curr) => pre + curr.quantity * curr.basePrice, 0);
  const totalRetailPrice = selectedProducts.reduce((pre, curr) => pre + curr.quantity * (curr.retailPrice ?? 0), 0);
  const selectedDealTotalCount = selectedProducts.reduce((pre, curr) => pre + curr.quantity, 0);

  const { defaultContentId, name, maxEa, minEa, mainImageUrl, exceptionLabel } = productDetailState;

  const [buyText, setBuyText] = useState('장바구니 담기');

  const { searchUserLocation } = useSearchLocation();
  const { addToBasket } = useCartItems();

  const dispatch = useDispatch();

  const isKurlyPass = exceptionLabel === 'KURLY_PASS';

  useEffect(() => {
    if (isPC && (isSoldOut || !isPurchaseStatus)) {
      setBuyText('상품준비중입니다');
      return;
    }

    if (isDirectOrder) {
      setBuyText('바로구매');
      return;
    }

    setBuyText('장바구니 담기');
  }, [isSoldOut, isPurchaseStatus, isDirectOrder]);

  const getInvalidQuantityMessage = (): string | null => {
    if (!head(selectedProducts)) {
      return '주문하실 상품을 선택해주세요.';
    }

    const totalQuantity = selectedProducts.reduce((prev, curr) => prev + curr.quantity, 0);

    if (minEa > totalQuantity) {
      return `${name}상품의 최소 구매 수량은 ${minEa}개입니다.`;
    }

    if (maxEa < totalQuantity) {
      return `${name}상품의 최대 구매 수량은 ${maxEa}개입니다.`;
    }

    return null;
  };

  // 장바구니 담기 이벤트 로깅
  const postAddToCartEvent = async () => {
    const fusionQueryId = getFusionQueryId(queryId);
    await eventAddToCartProduct({
      productDetailState,
      defaultContentId,
      selectedProducts,
      totalPrice,
      totalBasePrice,
      totalRetailPrice,
      fusionQueryId,
      filterId,
    });
    amplitudeService.syncLogEvent(
      new AddToCartSuccess({
        productDetailState,
        defaultContentId,
        totalPrice,
        totalBasePrice,
        totalRetailPrice,
        selectedDealTotalCount,
        fusionQueryId,
        filterId,
      }),
    );
  };

  // 장바구니 담기 완료 메세지
  const getToCartMessage = () => {
    const selectedProductsNo: number[] = selectedProducts.map(({ no }) => no);
    const isAlreadyAdded = cartItems.some(({ dealProductNo }) => selectedProductsNo.includes(dealProductNo));

    return isAlreadyAdded
      ? '장바구니에 상품을 담았습니다.\n이미 담은 상품의 수량을 추가했습니다.'
      : '선택한 상품이 장바구니에 담겼습니다.';
  };

  // 장바구니 담기
  const addToCart = async () => {
    const invalidQuantityMessage = getInvalidQuantityMessage();

    try {
      if (invalidQuantityMessage) {
        throw Error(invalidQuantityMessage);
      }

      const panelOption = {
        representativeItem: {
          imageUrl: mainImageUrl,
          name: selectedProducts[0].name,
        },
        isOpenPanel: true,
      };

      const items = selectedProducts.map((it) => ({
        dealProductNo: it.no,
        quantity: it.quantity,
      }));

      await addToBasket({
        cartItems: items,
        panelOption,
      });

      await postAddToCartEvent();

      if (!isPC) {
        Confirm({
          text: getToCartMessage(),
          leftButtonText: '장바구니 확인',
          rightButtonText: '계속 쇼핑하기',
          showRightButton: true,
          onClickLeftButton: () => dispatch(redirectTo({ url: getPageUrl(CART_PATH.cart) })),
          onClickRightButton: () => closeConfirm(),
        });
      }
    } catch (err) {
      amplitudeService.syncLogEvent(
        new AddToCartFail({
          fusionQueryId: getFusionQueryId(queryId),
        }),
      );

      dispatch(
        showAlert({
          title: '알림',
          message: err.message,
        }),
      );
    }
  };

  // 바로구매
  const addToCheckout = async () => {
    const targetProducts = selectedProducts.map((it) => ({
      dealProductNo: it.no,
      quantity: it.quantity,
    }));

    if (isEmpty(targetProducts)) {
      Alert({
        title: '알림',
        text: '주문하실 상품을 선택해주세요.',
      });
      return;
    }

    if (isGuest && isKurlyPass) {
      const returnUrl = encodeURIComponent(window.location.href);

      Alert({
        title: '알림',
        text: '비회원은 컬리패스 서비스를 이용하실 수 없습니다.',
        handleClickConfirmButton: () =>
          dispatch(redirectTo({ url: `${getPageUrl(COMMON_PATH.login)}?guest=1&return_url=${returnUrl}` })),
      });
      return;
    }

    const params = {
      dealProducts: targetProducts,
      address: currentAddress?.roadAddress ?? '',
      addressDetail: currentAddress?.addressDetail,
      deliveryPolicy: currentAddress?.deliveryType === 'direct' ? DeliveryTimeType.DAWN : DeliveryTimeType.DAY,
      memberReserveRatio,
      isDirectCheckout: true,
    };

    if (isGuest) {
      const returnUrl = encodeURIComponent(CART_PATH.counter.uri);

      storeCartCounter(params);

      dispatch(redirectTo({ url: `${getPageUrl(COMMON_PATH.login)}?guest=1&return_url=${returnUrl}` }));
      return;
    }

    const hasAddress = !!currentAddress?.address;

    if (!hasAddress) {
      searchUserLocation();
      return;
    }

    if (isKurlyPass) {
      dispatch(submitToCheckoutKurlyPass());
      return;
    }

    dispatch(submitToCheckout(params));

    const fusionQueryId = getFusionQueryId(queryId);

    await eventAddToCartProduct({
      productDetailState,
      defaultContentId,
      selectedProducts,
      totalPrice,
      totalBasePrice,
      totalRetailPrice,
      fusionQueryId,
      filterId,
    });
    amplitudeService.syncLogEvent(
      new AddToCartSuccess({
        productDetailState,
        defaultContentId,
        totalPrice,
        totalBasePrice,
        totalRetailPrice,
        selectedDealTotalCount,
        fusionQueryId,
        filterId,
      }),
    );
  };

  const buyProduct = async () => {
    if (isSoldOut) {
      return;
    }

    if (isDirectOrder) {
      await addToCheckout();
      return;
    }

    await addToCart();
  };

  return { buyText, buyProduct };
}
